/*
  Sticky add to cart driver.
  --------------------------
  Runs `sections/sticky-atc.liquid`. The bar is a mirror of the PDP buy panel, never a
  second one: it holds no variant state, owns no cart call, and the one action it offers
  is performed on the real button inside `[data-product-form]`.

    reads    `product-form:variant` from assets/product-form.js — the variant, or the
             checked colour's range while no size resolves one — and the panel's own
             checked radios
    adds     by clicking `[data-pf-submit]`, so assets/cart.js stays the one caller

  Markup contract — see sections/sticky-atc.liquid:
    [data-sticky-atc]        the bar root; `data-sa-size-index` / `data-sa-colour-index`
                             are -1 when the product has no such option
    [data-sa-price-current]  price text, mirroring the panel's own figure; the root's
                             `data-label-from` prefixes a colour's range
    [data-sa-image]          thumbnail, swapped from [data-sa-images] by colour value
    [data-sa-submit]         the add button
    [data-sa-status]         aria-live region for errors and confirmation

  No `[data-product-form]` on the page, or no `[data-pf-submit]` inside it, and the bar
  never shows: it would have nothing to mirror and nothing to click.
*/
(function () {
  if (window.__stickyAtc) return;
  window.__stickyAtc = true;

  function panelFor() {
    return document.querySelector('[data-product-form]');
  }

  /* The button this bar stands in for. Its disabled state is the one answer on whether the
     selection can be added — see renderSubmit. */
  function panelSubmitFor() {
    var panel = panelFor();
    return panel && panel.querySelector('[data-pf-submit]');
  }

  function optionIndex(bar, name) {
    var raw = parseInt(bar.getAttribute(name), 10);
    return isNaN(raw) ? -1 : raw;
  }

  /* The panel's checked radios, not the variant: they are still the truth when the
     combination resolves to nothing and there is no variant to read. */
  function selection(panel) {
    var groups = panel.querySelectorAll('[data-pf-option]');
    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var index = parseInt(groups[i].getAttribute('data-pf-option'), 10);
      var checked = groups[i].querySelector('input:checked');
      values[index] = checked ? checked.value : '';
    }
    return values;
  }

  function imagesFor(bar) {
    if (bar.__saImages) return bar.__saImages;
    var script = bar.querySelector('[data-sa-images]');
    var parsed = {};
    if (script) {
      try {
        parsed = JSON.parse(script.textContent) || {};
      } catch (e) {
        parsed = {};
      }
    }
    bar.__saImages = parsed;
    return parsed;
  }

  /* ---------- Rendering ---------- */

  /* Shopify's cart errors quote the variant title — "…Supernova / XXL / CN" — so a
     withheld origin would be printed on the bar. The panel substitutes its own wording for
     exactly this reason; see addFailed() in assets/product-form.js. */
  function errorFor(bar, message) {
    var panel = panelFor();
    var fallback = bar.getAttribute('data-error-fallback') || '';
    if (panel && panel.querySelector('[data-pf-auto-option]')) return fallback;
    return message || fallback;
  }

  function setStatus(bar, message, tone) {
    var status = bar.querySelector('[data-sa-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-success', tone === 'success');
  }

  /* One figure, as the design draws it — the panel's struck compare-at has no counterpart
     here, so the sale price is shown on its own. It takes `price`, not `priceShort`:
     the short form drops the currency code because the compare-at beside it carries it,
     and there is nothing beside it here. */
  function renderPrice(bar, variant, range) {
    var current = bar.querySelector('[data-sa-price-current]');
    if (!current) return;
    // No size yet: the colour's range as the panel shows it, "From" when its sizes differ.
    // Nothing to range either is left alone: blanking it would read as free.
    if (!variant && range) {
      var from = bar.getAttribute('data-label-from') || '';
      current.textContent = (range.varies && from ? from + ' ' : '') + range.variant.price;
      return;
    }
    if (!variant) return;
    current.textContent = variant.price;
  }

  /* Keyed on the colour value, not the variant: the thumbnail has to move the moment a
     colour is picked, including while the size is still unanswered and no variant
     resolves. A colour Shopify gave no image of keeps whichever is showing. */
  function renderImage(bar, panel) {
    var image = bar.querySelector('[data-sa-image]');
    var index = optionIndex(bar, 'data-sa-colour-index');
    if (!image || index < 0) return;

    var next = imagesFor(bar)[selection(panel)[index]];
    if (next && image.getAttribute('src') !== next) image.setAttribute('src', next);
  }

  /* Enabled with no variant when there is a size to ask for — the click sends the customer
     to the panel's picker, which a disabled button could not do.

     Whether a chosen variant can be added is the panel's answer rather than one taken
     again here: it alone counts what the cart already holds, and a size sold through in
     the cart is sellable by every measure this file can see. */
  function renderSubmit(bar, variant) {
    var submit = bar.querySelector('[data-sa-submit]');
    if (!submit) return;
    var label = submit.querySelector('.button__label');

    var panelSubmit = panelSubmitFor();
    var blocked = false;
    var state = 'add';

    if (!variant) {
      state = optionIndex(bar, 'data-sa-size-index') >= 0 ? 'select-size' : 'sold-out';
      blocked = state === 'sold-out';
    } else {
      blocked = panelSubmit ? panelSubmit.disabled : !variant.available;
      // `spent` is the panel blocking something it still reports as sellable — the last
      // unit is in the cart. Reading it back this way keeps the count in one file.
      if (variant.state === 'coming-soon') state = 'coming-soon';
      else if (!variant.available) state = 'sold-out';
      else if (blocked) state = 'max';
    }
    submit.disabled = blocked;
    submit.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    submit.classList.toggle('button--disabled', blocked);
    submit.classList.remove('is-loading');

    if (label) label.textContent = submit.getAttribute('data-label-' + state) || '';
  }

  function render(bar, variant, range) {
    var panel = panelFor();
    if (!panel) return;

    var previous = bar.__saVariant;
    bar.__saVariant = variant || null;
    renderPrice(bar, variant, range);
    renderImage(bar, panel);
    renderSubmit(bar, variant);

    /* Only a new selection makes a standing message stale. A failed add re-syncs the panel
       for the same variant, and clearing on every render would take the error with it —
       leaving the bar as the only CTA on screen with no reason it refused. */
    if ((previous && previous.id) !== (variant && variant.id)) setStatus(bar, '');
  }

  /* ---------- Add to cart ---------- */

  /* No size row in the design, so an unanswered size hands the customer back to the
     panel's own picker rather than answering it here. */
  function promptForSize(bar) {
    var panel = panelFor();
    var index = optionIndex(bar, 'data-sa-size-index');
    var group = panel && panel.querySelector('[data-pf-option="' + index + '"]');

    setStatus(bar, bar.getAttribute('data-error-select-size') || '', 'error');
    if (!group) return;

    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var input = group.querySelector('input[type="radio"]');
    // `preventScroll`, or focus would fight the smooth scroll just started.
    if (input && input.focus) input.focus({ preventScroll: true });
  }

  function submit(bar) {
    var panel = panelFor();
    if (!panel) return;
    // A colour swap is in flight (assets/pdp-render.js marks the bar): the panel this
    // would click still belongs to the old colour.
    if (bar.getAttribute('aria-busy') === 'true') return;

    if (!bar.__saVariant) {
      promptForSize(bar);
      return;
    }

    var target = panel.querySelector('[data-pf-submit]');
    if (!target) return;

    var button = bar.querySelector('[data-sa-submit]');

    // The bar can be a render behind the panel — a cart change from anywhere else moves
    // the panel without dispatching to here. Say why nothing happened rather than
    // swallowing the click; the button's own label already carries the reason.
    if (target.disabled) {
      var reason = button && button.querySelector('.button__label');
      setStatus(bar, (reason && reason.textContent) || bar.getAttribute('data-error-fallback') || '', 'error');
      return;
    }

    if (button) button.classList.add('is-loading');
    setStatus(bar, '');

    // The panel's own submit path runs from here: it is the only thing that talks to
    // assets/cart.js, and its result comes back as a cart event.
    target.click();
  }

  /* ---------- Visibility ---------- */

  function setVisible(bar, visible) {
    if (bar.__saVisible === visible) return;
    bar.__saVisible = visible;
    bar.classList.toggle('is-visible', visible);
  }

  /* Measured on scroll rather than watched with an IntersectionObserver: a jump — scroll
     restoration, an in-page anchor — can take the panel from below the fold to above it
     without ever intersecting, and no threshold is crossed to report. Same rAF-throttled
     shape as assets/pdp-sticky.js.

     The test is the panel's *bottom* edge, so the bar waits until the real one has gone
     up past the top of the viewport and never rises while it is still below the fold. The
     same pass parks it on the footer — see `footerLift`. */
  var watched = [];
  var queued = false;

  function refresh() {
    var panel = panelFor();
    if (!panel) return;

    var past = panel.getBoundingClientRect().bottom <= 0;
    var lift = footerLift();
    for (var i = 0; i < watched.length; i++) {
      if (watched[i].__saForced) continue;
      setLift(watched[i], lift);
      // Its whole box has passed the top of the screen: nothing to show, and nothing that
      // should still be tabbable.
      setVisible(watched[i], past && lift < window.innerHeight - insetOf(watched[i]));
    }
  }

  /* The bar is fixed, so it would otherwise sit over the footer and take its links with it.
     It rides up on the footer instead: the lift is however much of the footer is on screen,
     which keeps the bar's own bottom inset as the gap between the two — 48px on desktop,
     flush on mobile, the same spacing it holds against the bottom of the viewport. */
  function footerLift() {
    var footer =
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('.site-footer');
    if (!footer) return 0;

    return Math.max(0, window.innerHeight - footer.getBoundingClientRect().top);
  }

  function insetOf(bar) {
    return parseFloat(window.getComputedStyle(bar).bottom) || 0;
  }

  /* Published as a custom property rather than written into `transform`, so the CSS keeps
     ownership of the entry and exit slide and only composes the lift into it. */
  function setLift(bar, lift) {
    if (bar.__saLift === lift) return;
    bar.__saLift = lift;
    bar.style.setProperty('--sticky-atc-lift', lift + 'px');
    bar.classList.toggle('is-parked', lift > 0);
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      refresh();
    });
  }

  function watchVisibility(bar) {
    var panel = panelFor();
    // Nothing to mirror and nothing to click, so the bar stays down for good.
    if (!panel || !panel.querySelector('[data-pf-submit]')) return;
    watched.push(bar);
    refresh();
  }

  /* ---------- Wiring ---------- */

  function init(scope) {
    var bars = (scope || document).querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.__saReady) continue;
      bar.__saReady = true;

      var panel = panelFor();
      // `__pfVariant` is the panel driver's first pass, which runs before this file's own
      // DOMContentLoaded handler — so the opening state is read, not waited for.
      if (panel) render(bar, panel.__pfVariant || null);
      watchVisibility(bar);
    }
  }

  document.addEventListener('product-form:variant', function (e) {
    var bars = document.querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) {
      render(bars[i], e.detail && e.detail.variant, e.detail && e.detail.range);
    }
  });

  document.addEventListener('cart:updated', function (e) {
    if (!e.detail || e.detail.source !== 'product-form') return;

    /* A write carries the lines it wrote; a re-read carries none. The panel refreshes
       under this same source to retry a failed add, so confirming on every event would
       report success for an add that had just been rejected. */
    if (!e.detail.items || !e.detail.items.length) return;

    var bars = document.querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) {
      var button = bars[i].querySelector('[data-sa-submit]');
      if (!button) continue;
      button.classList.remove('is-loading');

      var added = button.getAttribute('data-label-added') || '';
      var label = button.querySelector('.button__label');
      if (label) label.textContent = added;
      setStatus(bars[i], added, 'success');
    }
    // Nothing resets this: the panel's driver re-syncs a couple of seconds after an add,
    // and that arrives here as `product-form:variant`.
  });

  document.addEventListener('cart:error', function (e) {
    if (!e.detail || e.detail.source !== 'product-form') return;

    var bars = document.querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) {
      var button = bars[i].querySelector('[data-sa-submit]');
      if (button) button.classList.remove('is-loading');
      setStatus(bars[i], errorFor(bars[i], e.detail.message), 'error');
    }
  });

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    if (!target.closest('[data-sa-submit]')) return;

    var bar = target.closest('[data-sticky-atc]');
    if (bar) submit(bar);
  });

  /* ---------- Lifecycle ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* The panel's height settles once its images have loaded, which moves the edge the bar
     is measured against. */
  window.addEventListener('load', refresh);

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    watched = watched.filter(function (bar) {
      return document.contains(bar) && !e.target.contains(bar);
    });
  });

  /* Selecting the section in the editor shows the bar wherever the page is scrolled to;
     nothing else would reveal it while the merchant is at the top of the PDP. */
  document.addEventListener('shopify:section:select', function (e) {
    var bars = e.target.querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) {
      bars[i].__saForced = true;
      setVisible(bars[i], true);
    }
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    var bars = e.target.querySelectorAll('[data-sticky-atc]');
    for (var i = 0; i < bars.length; i++) bars[i].__saForced = false;
    refresh();
  });
})();
